import { useEffect, useRef, useState } from 'react';
import { Alert, Button, Skeleton } from 'antd';
import { Link } from 'react-router-dom';

import { api } from '../api/client';
import { API_VERIFY_EMAIL_URL } from '../utils';
import { takeLinkToken } from '../utils/linkToken';

/**
 * Landing page for the `${APP_BASE_URL}/verify-email?token=…` link the API
 * mails out. Public: the endpoint takes no auth, and the recipient may well
 * open the link in a browser that has never signed in.
 *
 * The API answers a bad, expired, already-used or superseded token with one
 * flat 400 on purpose, so there is exactly one failure state to render.
 */
export default function VerifyEmail() {
	const [status, setStatus] = useState('verifying');

	// The token is single use: consuming it deletes the row, so a second POST
	// gets a 400. React 18 StrictMode runs effects twice in development, which
	// without this guard would let the second attempt's failure overwrite the
	// first attempt's success and show "invalid link" after a good verify.
	const fired = useRef(false);

	useEffect(() => {
		if (fired.current) return;
		fired.current = true;

		// Read and strip in one breath, before the request goes anywhere.
		const token = takeLinkToken();

		if (!token) {
			setStatus('failure');
			return;
		}

		api.post(API_VERIFY_EMAIL_URL, { token })
			.then(() => setStatus('success'))
			.catch(() => setStatus('failure'));
	}, []);

	return (
		<>
			<h1>Verify email</h1>

			{status === 'verifying' ? <Skeleton active /> : null}

			{status === 'success' ? (
				<>
					<Alert
						type="success"
						showIcon
						message="Email confirmed"
						description="Thanks — your email address is verified."
					/>
					<p style={{ marginTop: 16 }}>
						<Link to="/collections">
							<Button type="primary">Go to your collections</Button>
						</Link>
					</p>
				</>
			) : null}

			{status === 'failure' ? (
				<>
					<Alert
						type="error"
						showIcon
						message="This link is invalid or has expired"
						description="Verification links are good for seven days and can only be used once. Sign in and request a new one to finish verifying."
					/>
					<p style={{ marginTop: 16 }}>
						<Link to="/login">
							<Button type="primary">Sign in</Button>
						</Link>
					</p>
				</>
			) : null}
		</>
	);
}
